import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";

import { useToast } from "../hooks/useToast";
import {
  type ApiTask,
  type CreateTaskInput,
  type TaskCategory,
  createTask,
} from "../lib/api";

import { Button, Input, Modal, Select, Textarea } from "./ui";

interface TaskFormModalProps {
  mode: "create";
  initialCategory?: TaskCategory;
  quickInternal?: boolean;
  onClose: () => void;
  onSuccess?: (task: ApiTask) => void;
}

interface TaskFormState {
  category: TaskCategory;
  title: string;
  description: string;
  dueDate: string;
  priority: ApiTask["priority"];
  caseId: string;
  customerId: string;
}

export function TaskFormModal({
  initialCategory = "business",
  quickInternal = false,
  onClose,
  onSuccess,
}: TaskFormModalProps): JSX.Element {
  const [form, setForm] = useState(() => initialForm(initialCategory));
  const [titleError, setTitleError] = useState<string | undefined>();
  const queryClient = useQueryClient();
  const toast = useToast();
  const mutation = useMutation({
    mutationFn: () => createTask(buildInput(form, quickInternal)),
    onSuccess: async (task) => {
      await invalidateTaskQueries(queryClient);
      toast.success("タスクを追加しました");
      onSuccess?.(task);
      onClose();
    },
    onError: () => toast.error("タスクの追加に失敗しました"),
  });

  const submit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    const error = form.title.trim().length === 0 ? "タイトルは必須です" : undefined;
    setTitleError(error);
    if (error === undefined) mutation.mutate();
  };

  return (
    <Modal open title={quickInternal ? "社内タスク追加" : "タスク追加"} onClose={onClose} className="max-w-lg">
      <form onSubmit={submit} className="space-y-4">
        <TaskFields
          form={form}
          titleError={titleError}
          quickInternal={quickInternal}
          onChange={setForm}
        />
        <div className="flex justify-end gap-2 pt-1">
          <Button variant="secondary" onClick={onClose} disabled={mutation.isPending}>
            キャンセル
          </Button>
          <Button type="submit" loading={mutation.isPending}>
            追加
          </Button>
        </div>
      </form>
    </Modal>
  );
}

function TaskFields({
  form,
  titleError,
  quickInternal,
  onChange,
}: {
  form: TaskFormState;
  titleError: string | undefined;
  quickInternal: boolean;
  onChange: (form: TaskFormState) => void;
}): JSX.Element {
  return (
    <div className="space-y-3">
      {!quickInternal && <CategoryField form={form} onChange={onChange} />}
      <TaskField label="タイトル" error={titleError}>
        <Input value={form.title} onChange={(e) => onChange({ ...form, title: e.target.value })} />
      </TaskField>
      <div className="grid gap-3 sm:grid-cols-2">
        <TaskField label="期限日">
          <Input
            type="date"
            value={form.dueDate}
            onChange={(e) => onChange({ ...form, dueDate: e.target.value })}
          />
        </TaskField>
        {!quickInternal && <PriorityField form={form} onChange={onChange} />}
      </div>
      {form.category === "business" && !quickInternal && <ParentFields form={form} onChange={onChange} />}
      {!quickInternal && (
        <TaskField label="説明">
          <Textarea
            value={form.description}
            rows={3}
            onChange={(e) => onChange({ ...form, description: e.target.value })}
          />
        </TaskField>
      )}
    </div>
  );
}

function CategoryField({
  form,
  onChange,
}: {
  form: TaskFormState;
  onChange: (form: TaskFormState) => void;
}): JSX.Element {
  return (
    <fieldset className="space-y-2">
      <legend className="text-xs font-medium text-text-secondary">種別</legend>
      <div className="flex flex-wrap gap-3">
        <RadioOption label="業務" checked={form.category === "business"} onChange={() => onChange({ ...form, category: "business" })} />
        <RadioOption label="社内" checked={form.category === "internal"} onChange={() => onChange({ ...form, category: "internal" })} />
      </div>
    </fieldset>
  );
}

function RadioOption({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: () => void;
}): JSX.Element {
  return (
    <label className="inline-flex items-center gap-2 text-sm text-text-primary">
      <input type="radio" checked={checked} onChange={onChange} className="h-4 w-4 accent-primary" />
      <span>{label}</span>
    </label>
  );
}

function PriorityField({
  form,
  onChange,
}: {
  form: TaskFormState;
  onChange: (form: TaskFormState) => void;
}): JSX.Element {
  return (
    <TaskField label="優先度">
      <Select
        value={form.priority}
        onChange={(e) => onChange({ ...form, priority: e.target.value as ApiTask["priority"] })}
      >
        <option value="high">高</option>
        <option value="medium">中</option>
        <option value="low">低</option>
      </Select>
    </TaskField>
  );
}

function ParentFields({
  form,
  onChange,
}: {
  form: TaskFormState;
  onChange: (form: TaskFormState) => void;
}): JSX.Element {
  return (
    <div className="grid gap-3 sm:grid-cols-2">
      <TaskField label="案件ID">
        <Input value={form.caseId} onChange={(e) => onChange({ ...form, caseId: e.target.value })} />
      </TaskField>
      <TaskField label="顧客ID">
        <Input value={form.customerId} onChange={(e) => onChange({ ...form, customerId: e.target.value })} />
      </TaskField>
    </div>
  );
}

function TaskField({
  label,
  error,
  children,
}: {
  label: string;
  error?: string | undefined;
  children: JSX.Element;
}): JSX.Element {
  return (
    <div className="block text-xs font-medium text-text-secondary">
      <span>{label}</span>
      <div className="mt-1">{children}</div>
      {error !== undefined && <p className="mt-1 text-xs text-danger">{error}</p>}
    </div>
  );
}

function initialForm(category: TaskCategory): TaskFormState {
  return {
    category,
    title: "",
    description: "",
    dueDate: todayDateInputValue(),
    priority: "medium",
    caseId: "",
    customerId: "",
  };
}

function buildInput(form: TaskFormState, quickInternal: boolean): CreateTaskInput {
  const category: TaskCategory = quickInternal ? "internal" : form.category;
  const input: CreateTaskInput = {
    title: form.title.trim(),
    category,
    priority: "medium",
    status: "open",
  };
  if (form.dueDate.length > 0) input.due_date = `${form.dueDate}T23:59:59.999+09:00`;
  if (!quickInternal) addOptionalFields(input, form);
  return input;
}

function addOptionalFields(input: CreateTaskInput, form: TaskFormState): void {
  input.priority = form.priority;
  const description = form.description.trim();
  if (description.length > 0) input.description = description;
  if (form.category !== "business") return;
  const caseId = form.caseId.trim();
  const customerId = form.customerId.trim();
  if (caseId.length > 0) input.case_id = caseId;
  if (customerId.length > 0) input.customer_id = customerId;
}

async function invalidateTaskQueries(queryClient: ReturnType<typeof useQueryClient>): Promise<void> {
  await queryClient.invalidateQueries({ queryKey: ["today"] });
  await queryClient.invalidateQueries({ queryKey: ["tasks"] });
  await queryClient.invalidateQueries({ queryKey: ["dashboard"] });
  await queryClient.invalidateQueries({ queryKey: ["overdue-count"] });
}

function todayDateInputValue(): string {
  const formatter = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Tokyo",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
  return formatter.format(new Date());
}
